import React from 'react';
import { ArrowRightIcon, StarIcon } from 'lucide-react';
import { CtaButton } from '../CtaButton';
import { SectionBadge } from '../SectionBadge';
import { HERO_FEATURES, HERO_MOCKUP } from '../../data/content';

/**
 * Above-the-fold content renders immediately (no scroll animation) so the
 * headline, mockup and CTA paint as fast as possible.
 */
export function Hero({
  onCta
}: {
  onCta: () => void;
}) {
  return <section className="relative w-full px-5 pb-12 pt-8 sm:pb-16 sm:pt-10" data-id="element-139" data-mp="mt92s55bd4gh">
      <div className="relative mx-auto flex max-w-3xl flex-col items-center text-center" data-id="element-140" data-mp="qv45ne2w7pwd">
        <SectionBadge data-id="element-141" data-mp="vlu74g8778ls">🩰 Para profesoras de danza clásica</SectionBadge>

        <h1 className="mt-5 font-display text-[30px] leading-[1.15] text-snow sm:text-4xl lg:text-5xl" data-id="element-142" data-mp="voot4c6v11hb">
          <span className="block" data-id="element-143" data-mp="15kid2gmwrx94">
            Resolvé todo tu <em className="italic u-lime" data-id="element-144" data-mp="15ltfac0pgwy4">año de danza clásica</em>
          </span>
          <span className="mt-1 block" data-id="element-145" data-mp="1aitpmz4l8lv1">
            en <strong className="not-italic text-lime" data-id="element-146" data-mp="1ahikauvhv61t">5 minutos</strong>
          </span>
        </h1>

        <div className="mt-7 w-full" data-id="element-147" data-mp="9lpymht6h3xc">
          <div className="mx-auto max-w-lg rounded-3xl bg-ink p-2.5 shadow-[0_18px_40px_rgba(0,0,0,0.3)] sm:p-3" data-id="element-148" data-mp="4o6nvrvov9a5">
            <img src={HERO_MOCKUP} alt="Megapack Danza Clásica Pro con los 6 tomos y los 5 bonos incluidos" width={1024} height={1024} loading="eager" fetchPriority="high" decoding="async" className="aspect-square w-full rounded-2xl" data-id="element-149" data-mp="3vu96zhppyejf" />
          </div>
        </div>

        <p className="mx-auto mt-6 max-w-xl text-sm leading-relaxed text-snow sm:text-base" data-id="element-150" data-mp="inzc9gjqtdey">
          <span className="block" data-id="element-151" data-mp="enfeqbv9c7sa">
            <strong className="font-bold" data-id="element-152" data-mp="eme1q1uolb7x">6 tomos completos</strong> y{' '}
            <strong className="font-bold" data-id="element-153" data-mp="1b51t2emmhv4z">5 bonos exclusivos</strong>.
          </span>
          <span className="block" data-id="element-154" data-mp="16djs3qklnuk0">
            Planificá, enseñá y <span className="u-lime font-semibold" data-id="element-155" data-mp="16byz45dxojo9">renová tus clases</span> todo el año.
          </span>
          <span className="block text-snow-muted" data-id="element-156" data-mp="30o0clji8ledk">Sin volver a improvisar un domingo a la noche.</span>
        </p>

        <div className="mt-7 w-full" data-id="element-157" data-mp="2qw44dfmfs7vm">
          <CtaButton variant="lime" className="w-full max-w-sm" onClick={onCta} data-id="element-158" data-mp="2qv2sj8xljhcu">
            Quiero mi Megapack <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-159" data-mp="2vu5ng0jv4773" />
          </CtaButton>
          <p className="mt-2.5 text-xs font-semibold text-snow-muted" data-id="element-160" data-mp="2hv67riebv2n2">
            Acceso inmediato · Pago seguro · <span className="text-snow" data-id="element-161" data-mp="2mvk6uaxyb28y">Garantía de 7 días</span>
          </p>
        </div>

        <div className="mt-7 w-full" data-id="element-162" data-mp="27y1mdksb3b7b">
          <div className="mx-auto flex max-w-xl flex-wrap items-center justify-center gap-x-6 gap-y-2.5" data-id="element-163" data-mp="27z2v3ilunllv">
            {HERO_FEATURES.map(feature => <span key={feature.label} className="inline-flex items-center gap-2 text-sm font-bold text-snow" data-id="element-164" data-mp="65n5mp1i0csh">
                <span aria-hidden="true" data-id="element-165" data-mp="b3x356ap3ph9">{feature.icon}</span>
                {feature.label}
              </span>)}
          </div>
        </div>

        <div className="mt-6 inline-flex items-center gap-3 rounded-full bg-ink px-5 py-2.5" data-id="element-166" data-mp="1qnxoshmzu2u6">
          <span className="flex gap-0.5" aria-label="Calificación 4.9 de 5" data-id="element-167" data-mp="1qm34n5jjqer5">
            {Array.from({
            length: 5
          }).map((_, index) => <StarIcon key={index} className="h-3.5 w-3.5 fill-lime text-lime" aria-hidden="true" data-id="element-168" data-mp="20lu0bajuezst" data-mpi={index} />)}
          </span>
          <span className="text-xs font-bold text-snow" data-id="element-169" data-mp="2ebozjgsvy7tc">
            4.9/5 · <span className="text-lime" data-id="element-170" data-mp="2acoj24xvrh9i">+500 profesoras</span>
          </span>
        </div>
      </div>
    </section>;
}